<script setup lang="ts">
import CustomTab from "@/components/custom/CustomTab.vue";
import {useRoute, useRouter} from "vue-router";
import {onMounted, ref, watch} from "vue";
import CommonButton from "@/components/common/CommonButton.vue";
import {Edit02, Eye, EyeOff, Plus, Trash01} from "utruckin-icons"
import CommonTable from "@/components/common/CommonTable.vue";
import {ITableHeader} from "@/types/table";
import CommonInput from "@/components/common/CommonInput.vue";
import {useAuthStore} from "@/features/auth/auth.store";
import {IProfile} from "@/features/auth/auth.types";
import {IAdminUser} from "@/features/admin/admin.types";
import {useAdminStore} from "@/features/admin/admin.store";
import CustomBadge from "@/components/custom/CustomBadge.vue";
import {useAlert} from "@/utils/alert";
import CommonModal from "@/components/common/CommonModal.vue";
import {useAppStore} from "@/store/app.store";

interface IUserModal {
  role: 'viewer' | 'admin';
  email: string;
  password: string;
  confirmPassword: string;
  id: number;
}

const route = useRoute();
const router = useRouter();
const authStore = useAuthStore();
const adminStore = useAdminStore();
const {show} = useAlert()
const appStore = useAppStore();

const activeTab = ref<number>(1);
const typeNewPasswordInput = ref<"password" | "text">("password");
const typeConfirmPasswordInput = ref<"password" | "text">("password");
const profileLoading = ref<boolean>(false);
const usersLoading = ref<boolean>(false);

const usersList = ref<IAdminUser[]>([]);

const role = ref<string>("");
const email = ref<string>("");
const user = ref<IProfile | null>(null);
const password = ref<string>("");
const confirmPassword = ref<string>("");

const editingModal = ref<boolean>(false);
const modalTitle = ref<string>("");
const modalItem = ref<IUserModal>({
  role: "viewer",
  email: "",
  password: "",
  confirmPassword: "",
  id: 0
})
const modalPasswordType = ref<'password' | 'text'>("password");
const modalConfirmPasswordType = ref<'password' | 'text'>("password");

const deletingModal = ref<boolean>(false);
const deleteModalItem = ref<any>()

const tabs = [
  {label: "Profile", value: 1},
  {label: "Users", value: 2},
]

const roleTabs = [
  {label: "Viewer", value: 'viewer',},
  {label: "Admin", value: 'admin',}
]

const headers: ITableHeader[] = [
  {
    name: 'id',
    label: 'No',
    fitContent: true
  },
  {
    name: 'email',
    label: 'Email',
    width: '300px'
  },
  {
    name: 'role',
    label: 'Role',
    width: '300px'
  },
  {
    name: 'is_active',
    label: 'Status',
    width: '300px'
  },
  {
    name: 'actions',
    label: 'Actions',
    fitContent: true
  }
]

const handleAppendClickNewPassword = () => {
  if (typeNewPasswordInput.value === "password") {
    typeNewPasswordInput.value = "text"

  } else {
    typeNewPasswordInput.value = "password"
  }
}

const handleAppendClickConfirmPassword = () => {
  if (typeConfirmPasswordInput.value === "password") {
    typeConfirmPasswordInput.value = "text"

  } else {
    typeConfirmPasswordInput.value = "password"
  }
}

const getProfile = async () => {
  user.value = await authStore.getProfile(profileLoading);
}

const updateProfile = async () => {
  let payload: IAdminUser = {
    email: user.value?.email as string,
    role: user.value?.role as 'admin' | 'viewer',
    password: password.value,
    is_active: user.value?.is_active as boolean
  }
  let response = await adminStore.updateUser(profileLoading, payload, user.value?.id as number)

  if (response.status == 'updated') {
    show("Saved successfully", "success");
    password.value = "";
    confirmPassword.value = "";
  }
}

const getUsers = async () => {
  usersList.value = await adminStore.getUsers(usersLoading);
}

const closeModal = () => {
  modalTitle.value = "";
  modalItem.value = {
    role: "viewer",
    email: "",
    password: "",
    confirmPassword: "",
    id: 0
  }
}

const editAdminUser = (e: any, user?: IAdminUser) => {
  if (user) {
    modalTitle.value = "Edit user";
    modalItem.value = {
      role: user.role,
      email: user.email,
      password: "",
      confirmPassword: "",
      id: user.id as number
    }
  } else {
    modalTitle.value = "Add user";
    modalItem.value = {
      role: "viewer",
      email: "",
      password: "",
      confirmPassword: "",
      id: 0
    }
  }

  editingModal.value = true;
}

const cancelModalButton = () => {
  editingModal.value = false;
  closeModal()
}

const saveModalButton = async () => {
  if (
      !modalItem.value.email &&
      !modalItem.value.password &&
      modalItem.value.password !== modalItem.value.confirmPassword
  ) {
    return;
  }
  let result;
  let request = {
    email: modalItem.value.email,
    password: modalItem.value.password,
    role: modalItem.value.role,
    is_active: true
  }
  if (modalTitle.value === "Add user") {
    result = await adminStore.createUser(usersLoading, request)
  } else {
    result = await adminStore.updateUser(usersLoading, request, modalItem.value.id)
  }

  if (result) {
    show("Saved successfully", "success");
    cancelModalButton()
    appStore.triggerRefresh()
  }
}

const openDeleteModal = (item: IAdminUser) => {
  deletingModal.value = true;
  deleteModalItem.value = item;
}

const closeDeleteModal = () => {
  deleteModalItem.value = undefined;
}

const cancelDeleteButton = () => {
  deletingModal.value = false;
}

const deleteUser = async () => {
  let response = await adminStore.deleteUser(usersLoading, deleteModalItem.value.id);

  if (response.status === "deactivated") {
    show("Deactivated successfully", "success");
    cancelDeleteButton()
    appStore.triggerRefresh()
  }
}

onMounted(() => {
  if (route.query.name === "users") {
    activeTab.value = 2;
    getUsers()
  } else {
    activeTab.value = 1;
    getProfile()
  }
})

watch(
    () => activeTab.value,
    (newVal, oldVal) => {
      if (newVal !== oldVal) {
        if (newVal == 1) {
          router.push({
            path: "/settings",
            query: {
              name: 'profile'
            }
          })
        } else {
          router.push({
            path: "/settings",
            query: {
              name: 'users'
            }
          })
        }
      }
    }
)

watch(
    () => route.query.name,
    (newVal) => {
      if (newVal === 'users') {
        activeTab.value = 2;
        getUsers()
      } else {
        activeTab.value = 1;
        getProfile()
      }
    }
)

watch(
    () => appStore.refreshKey,
    () => {
      if (route.query.name === "users") {
        getUsers()
      } else {
        getProfile()
      }
    },
    {
      immediate: false,
    }
)
</script>

<template>
  <div class="v-settings">
    <div class="header" :class="activeTab == 1 ? 'border' : ''">
      <CustomTab :tabs="tabs" v-model="activeTab"/>

      <CommonButton
          v-if="activeTab == 2"
          prepend
          :prepend-icon="Plus"
          type="primary"
          label="Add user"
          @click="editAdminUser"
      />
    </div>
    <div class="body">
      <div class="profile" v-if="activeTab == 1">

        <div class="profile__info">
          <div class="profile__info--header">
            Profile info
          </div>

          <CommonInput
              v-if="user"
              label="Role"
              placeholder="Role"
              v-model:model-value="user.role"
              disabled

          />
          <CommonInput
              v-if="user"
              label="Email"
              placeholder="Email"
              v-model:model-value="user.email"
              disabled
          />
        </div>

        <div class="profile__password">
          <div class="profile__password--header">
            New password
          </div>

          <div class="profile__password--body">
            <CommonInput
                :type="typeNewPasswordInput"
                v-model:model-value="password"
                label="New password"
                placeholder="New password"
                :append-icon="typeNewPasswordInput == 'password' ? Eye : EyeOff"
                @append-icon-click="handleAppendClickNewPassword"
                :error="password.length < 6"
                errorMessage="Password should have at least 6 characters"
            />

            <CommonInput
                :type="typeConfirmPasswordInput"
                v-model:model-value="confirmPassword"
                label="Confirm password"
                placeholder="Confirm password"
                :append-icon="typeConfirmPasswordInput == 'password' ? Eye : EyeOff"
                @append-icon-click="handleAppendClickConfirmPassword"
                :error="password !== confirmPassword"
                errorMessage="Passwords must be the same"
            />

            <CommonButton
                type="primary"
                label="Save password"
                :is-disabled="password !== confirmPassword || (!password) || password.length < 6"
                @click="updateProfile"
            />
          </div>
        </div>
      </div>

      <div class="users" v-else>
        <CommonTable :loading="usersLoading" :headers="headers" :items="usersList">
          <template #id="{index}">
            {{ index + 1 }}
          </template>
          <template #is_active="{item}">
            <CustomBadge
                :title="item['is_active'] ? 'Active' : 'Inactive'"
                :type="item['is_active'] ? 'success' : 'danger'"
            ></CustomBadge>
          </template>
          <template #actions="{item}">
            <div class="actions">
              <div class="actions__edit">
                <Edit02 :size="16" @click="(e) => editAdminUser(e, item as IAdminUser)"/>
              </div>
              <div class="actions__delete">
                <Trash01 :size="16" @click="openDeleteModal(item)"/>
              </div>
            </div>
          </template>
        </CommonTable>
      </div>
    </div>
  </div>

  <CommonModal v-model="editingModal" :title="modalTitle" @close="closeModal">
    <div>
      <div style="display: flex; margin-bottom: 12px">
        <CustomTab :tabs="roleTabs" v-model="modalItem.role"/>
      </div>

      <CommonInput
          label="Email"
          placeholder="Email"
          v-model:model-value="modalItem.email"
          :error="!modalItem.email"
          errorMessage="Value is required"
      />

      <CommonInput
          :type="modalPasswordType"
          v-model:model-value="modalItem.password"
          label="New password"
          placeholder="New password"
          :append-icon="modalPasswordType == 'password' ? Eye : EyeOff"
          @append-icon-click="() => {
            modalPasswordType = modalPasswordType == 'password' ? 'text' : 'password'
          }"
          :error="modalItem.password.length < 6"
          errorMessage="Password should have at least 6 characters"
      />

      <CommonInput
          :type="modalConfirmPasswordType"
          v-model:model-value="modalItem.confirmPassword"
          label="Confirm password"
          placeholder="Confirm password"
          :append-icon="modalConfirmPasswordType == 'password' ? Eye : EyeOff"
          @append-icon-click="() => {
            modalConfirmPasswordType = modalConfirmPasswordType == 'password' ? 'text' : 'password'
          }"
          :error="modalItem.password !== modalItem.confirmPassword"
          errorMessage="Passwords must be the same"
      />
    </div>

    <template #footer>
      <div class="modal__footer">
        <CommonButton type="secondary" label="Cancel" @clicked="cancelModalButton"/>
        <CommonButton type="primary" label="Save" @clicked="saveModalButton"/>
      </div>
    </template>
  </CommonModal>


  <CommonModal v-model="deletingModal" @close="closeDeleteModal">
    <div>
      Are you sure you want to deactivate this user? This action cannot be undone.
    </div>

    <template #footer>
      <div class="modal__footer">
        <CommonButton type="secondary" label="Cancel" @clicked="cancelDeleteButton"/>
        <CommonButton type="primary" label="Save" @clicked="deleteUser"/>
      </div>
    </template>
  </CommonModal>
</template>

<style scoped lang="scss">
.v-settings {
  .header {
    padding: 16px;
    display: flex;
    justify-content: space-between;
    align-items: center;

    &.border {
      border-bottom: 1px solid var(--color-border);
    }
  }

  .body {
    .profile {
      &__info, &__password {
        padding: 16px;
        display: flex;
        flex-direction: column;
        justify-content: start;
        align-items: start;
        gap: 16px;
        border-bottom: 1px solid var(--color-border);

        &--header {
          font-weight: 700;
          color: var(--color-label);
        }

        &--body {
          display: flex;
          flex-direction: column;
          justify-content: start;
          align-items: start;
        }
      }

    }

    .users {
      padding: 0 16px;

      .actions {
        display: flex;
        align-items: center;
        gap: 6px;

        &__edit, &__delete {
          width: 31px;
          height: 31px;
          border-radius: 50%;
          border: 1px solid var(--color-border);
          background-color: var(--color-bg-secondary);
          display: flex;
          align-items: center;
          justify-content: center;
          position: relative;

          &:hover {
            background-color: var(--color-bg-tertiary);
          }
        }
      }
    }
  }
}

.modal__footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
}
</style>